import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Clock, Ticket, CheckCircle2, Send, QrCode } from "lucide-react";
import { WHATSAPP_DISPLAY } from "../lib/whatsapp";

const CATEGORIES = ["Normal", "Premium", "VIP"];

const inputCls =
  "w-full border-b border-white/20 bg-transparent py-4 text-base text-white placeholder-white/30 outline-none transition-colors duration-300 focus:border-gold";

export default function SoireeVip() {
  const initial = { name: "", phone: "", email: "", categorie: "Normal", personnes: "1", message: "" };
  const [form, setForm] = useState(initial);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [ticket, setTicket] = useState("");
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.phone.trim()) {
      setError("Merci d'indiquer votre nom et votre numéro WhatsApp pour réserver.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reservation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setTicket(data.ticket || "");
      setSent(true);
    } catch {
      setError("Une erreur est survenue, merci de réessayer ou de nous contacter sur WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grain relative min-h-screen bg-[#0a0a0a] px-6 py-20" data-testid="vip-page">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[380px] w-[680px] -translate-x-1/2 rounded-full bg-gold/10 blur-[120px]" />
      <div className="relative mx-auto max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="text-center"
        >
          <p className="text-[11px] font-bold uppercase tracking-[0.4em] text-gold">FK Conseil &amp; Événement</p>
          <h1 className="mt-6 font-display text-5xl tracking-tight text-white sm:text-6xl" data-testid="vip-title">
            AFTER SOIRÉE <em className="italic text-gold">VIP</em>
          </h1>
          <p className="mt-3 text-sm font-bold uppercase tracking-[0.25em] text-white">Chez Ousman Gouzele</p>
          <p className="mt-1 text-[10px] uppercase tracking-[0.3em] text-white/40">After Accor Arena JB Mpiana</p>

          <div className="mx-auto mt-8 inline-block border-2 border-gold px-8 py-3" data-testid="vip-date">
            <p className="text-sm font-bold uppercase tracking-[0.3em] text-white">
              Samedi <span className="font-display text-4xl normal-case tracking-normal text-gold">14</span> Novembre
            </p>
            <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.25em] text-gold">Jusqu'à 5h du matin</p>
          </div>

          <div className="mx-auto mt-8 flex max-w-md items-start justify-center gap-8 text-left">
            <div className="flex items-start gap-2">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-white">Factory58</p>
                <p className="text-[11px] leading-snug text-white/60">
                  12 rue Gutenberg, 93000 Bobigny
                  <br />
                  à proximité de Paris
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 shrink-0 text-gold" />
              <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-white">
                Jusqu'à 5H
                <span className="block text-[9px] font-normal text-white/50">du matin</span>
              </p>
            </div>
          </div>

          <p className="mt-6 font-display text-lg italic text-white/60">Venez vivre une soirée inoubliable</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 rounded-3xl border border-white/10 bg-[#141414]/80 p-8 backdrop-blur-xl lg:p-10"
        >
          {sent ? (
            <div className="py-10 text-center" data-testid="vip-success">
              <CheckCircle2 className="mx-auto h-14 w-14 text-gold" />
              <h2 className="mt-6 font-display text-3xl tracking-tight text-white">
                Réservation <em className="italic text-gold">reçue !</em>
              </h2>
              <p className="mx-auto mt-4 max-w-sm text-sm leading-relaxed text-white/55">
                Votre place pour la soirée du 14 novembre est enregistrée. Votre billet sécurisé avec QR code est prêt —
                présentez-le à l'entrée.
              </p>
              {ticket && (
                <a
                  href={ticket}
                  data-testid="vip-ticket-link"
                  className="mt-8 inline-flex items-center gap-3 rounded-full bg-gold px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-[#0a0a0a] transition-transform duration-300 hover:scale-105"
                >
                  <QrCode className="h-4 w-4" />
                  Voir mon billet
                </a>
              )}
              <div>
                <button
                  type="button"
                  onClick={() => { setForm(initial); setSent(false); setTicket(""); }}
                  data-testid="vip-new-reservation"
                  className="mt-6 rounded-full border border-gold/60 px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-gold transition-colors duration-300 hover:bg-gold hover:text-[#0a0a0a]"
                >
                  Nouvelle réservation
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-8" data-testid="vip-form">
              <div className="flex items-center gap-3">
                <Ticket className="h-5 w-5 text-gold" />
                <h2 className="font-display text-2xl tracking-tight text-white">
                  Réserver <em className="italic text-gold">ma place</em>
                </h2>
              </div>
              <div>
                <label className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.3em] text-white/40">Votre nom</label>
                <input type="text" value={form.name} onChange={set("name")} required placeholder="Ex. Marie N." data-testid="vip-input-name" className={inputCls} />
              </div>
              <div>
                <label className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.3em] text-white/40">Votre WhatsApp</label>
                <input type="tel" value={form.phone} onChange={set("phone")} required placeholder="06 12 34 56 78" data-testid="vip-input-phone" className={inputCls} />
              </div>
              <div>
                <label className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.3em] text-white/40">Votre email (pour recevoir votre billet)</label>
                <input type="email" value={form.email} onChange={set("email")} placeholder="vous@exemple.com" data-testid="vip-input-email" className={inputCls} />
              </div>
              <div className="grid gap-8 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.3em] text-white/40">Catégorie</label>
                  <select value={form.categorie} onChange={set("categorie")} data-testid="vip-select-categorie" className={`${inputCls} cursor-pointer bg-[#141414]`}>
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c} className="bg-[#141414] text-white">{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.3em] text-white/40">Personnes</label>
                  <input type="number" min="1" value={form.personnes} onChange={set("personnes")} required data-testid="vip-input-personnes" className={inputCls} />
                </div>
              </div>
              <div>
                <label className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.3em] text-white/40">Message (optionnel)</label>
                <textarea value={form.message} onChange={set("message")} rows={3} placeholder="Une demande particulière…" data-testid="vip-input-message" className={`${inputCls} resize-none`} />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="vip-submit-button"
                className="group flex w-full items-center justify-center gap-3 rounded-full bg-gold py-5 text-xs font-bold uppercase tracking-[0.25em] text-[#0a0a0a] transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
              >
                {sending ? "Envoi en cours..." : "Confirmer ma réservation"}
                {!sending && <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
              </button>
              <p className="text-center text-xs text-white/40">
                Réservation en ligne uniquement — billet sécurisé avec QR code délivré immédiatement.
              </p>
              {error && (
                <p className="rounded-xl border border-red-400/40 bg-red-400/10 px-4 py-3 text-center text-xs font-semibold text-red-400" data-testid="vip-error">{error}</p>
              )}
            </form>
          )}
        </motion.div>

        <p className="mt-10 text-center">
          <a href="/" data-testid="vip-back-link" className="text-[10px] uppercase tracking-[0.3em] text-white/40 transition-colors hover:text-gold">
            ← Retour au site
          </a>
        </p>
      </div>
    </div>
  );
}
